/**
 * Route loaders for admin user detail pages using React Query
 * @module loaders
 */
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, defer } from 'react-router-dom';
import { graphqlFetcher } from '~/utils/graphql-fetcher';

import {
  UserByRowIdDocument,
  UserByRowIdQueryResult,
  UserByRowIdQueryVariables,
  useUserByRowIdQuery,
} from '../utils/gql-rq/get-user-for-dashboard.gql-rq.hooks';

/**
 * Loader for admin user detail routes that prefetches user data
 * Uses React Query to cache and prefetch user data before rendering
 *
 * @param params - Route parameters containing user ID
 * @param queryClient - React Query client instance
 * @returns Promise resolving to prefetched user data
 */
export const createAdminUserDetailLoader = (queryClient: QueryClient) => {
  return async ({ params }: LoaderFunctionArgs) => {
    const userId = params.id;

    if (!userId) {
      throw new Error('User ID is required');
    }

    const rowId = Number(userId);
    if (!Number.isInteger(rowId)) {
      throw new Error('Invalid user ID');
    }

    const variables: UserByRowIdQueryVariables = { rowId };
    const queryKey = useUserByRowIdQuery.getKey(variables);

    const userDataPromise = queryClient.fetchQuery({
      queryKey,
      queryFn: graphqlFetcher<UserByRowIdQueryResult, UserByRowIdQueryVariables>(
        UserByRowIdDocument,
        variables,
      ),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    });

    return defer({ userData: userDataPromise });
  };
};

/**
 * Type for the data returned by the loader
 */
export interface AdminUserDetailLoaderData {
  userData: Promise<UserByRowIdQueryResult>;
}
